import { Rows } from 'src/app/domain/excel/interfaces';
import { FinancialStatementItem } from 'src/app/domain/financial-statement/models/common/FinancialStatementItem';
import { FinancialStatementPart } from 'src/app/domain/financial-statement/models/common/FinancialStatementPart';
import { BsExcelExportData } from './bsTemplate';
import { ColumnDate } from 'src/app/domain/financial-statement/utils/common/utilityClasses';
import { formats, getCell, withCellStyles } from 'src/app/domain/excel/utils';
import { boldGrayBgLeft, boldLightGrayBgLeft, left, leftBold } from 'src/app/util/excelStyles';
import { ColumnOrder } from '../../domain/financial-statement/component/bs-page/bs-page.component';

export const getPreparedData = (data: BsExcelExportData, headers: string[], headersForDisplay: string[]) => {
  const newData: Rows = [];
  const headerRow = [];
  headersForDisplay.forEach((header) => headerRow.push(withCellStyles(header, leftBold)));

  newData.push(headerRow);
  data.parts.forEach((part, i) => {
    const partRow = getPartRow(part, headers, currentIndex);
    newData.push(...partRow);
    currentIndex += 1;
    const itemsRows = getItemRows(part.financialStatementItems, data.selectedBasedDataColumns, headers, currentIndex);
    newData.push(...itemsRows);
    currentIndex += itemsRows.length;
    const subTotalRow = getSubTotalRow(part, headers, currentIndex);
    newData.push(...subTotalRow);
    currentIndex += 1;
    if (i < data.parts.length - 1) {
      newData.push([]);
    }
    currentIndex += 1;

  });
  const checkRow = getGrandTotalRow(headers);
  newData.push(...checkRow);
  partIndexArray = [];
  currentIndex = 1;
  partnersCapitalIndicator = null;
  return newData;
};


export const getPartRow = (part: FinancialStatementPart, headers: string[], currentIndex: number) => {
  const rows: Rows = [];
  const row = [];
  const body = getPartMapping(part);
  for (const header of headers) {
    row.push(withCellStyles(body[header], leftBold));
  }
  rows.push(row);
  return rows;
};

export const getItemRows = (items: FinancialStatementItem[], selectedBasedDataColumns: Map<ColumnOrder, ColumnDate>, headers: string[], currentIndex: number) => {
  const rows: Rows = [];
  items.forEach((item) => {
    currentIndex++;
    const row = [];
    const mapping = getItemMapping(item, selectedBasedDataColumns, currentIndex);
    for (const header of headers) {
      row.push(withCellStyles(mapping[header], left));
    }
    rows.push(row);
  });

  return rows;

};

export const getSubTotalRow = (part: FinancialStatementPart, headers: string[], currentIndex: number) => {
  const rows: Rows = [];
  const row = [];

  if (part.partName.toLocaleLowerCase().includes('partners capital')) {
    partnersCapitalIndicator = currentIndex + 1;
  } else {
    partIndexArray.push(currentIndex + 1);
  }
  const subTotal = getSubTotalMapping(part, currentIndex);
  for (const header of headers) {
    row.push(withCellStyles(subTotal[header], boldLightGrayBgLeft));
  }
  rows.push(row);
  return rows;
};

export const getPartMapping = (part: FinancialStatementPart) => {
  const body = {
    '': part.partName,
    'Date(0)': '',
    'Difference(0)': '',
    'Date(1)': '',
    'Difference(1)': '',
    'Date(2)': '',
    'Difference(2)': '',
    'Date(3)': '',
  };
  return body;
};

export const getItemMapping = (item: FinancialStatementItem, selectedBasedDataColumns: Map<ColumnOrder, ColumnDate>, currentIndex: number) => {
  const firstColumnValue = item.sums.dateColumnSums.get(selectedBasedDataColumns.get(ColumnOrder.FIRST).selectedDate);
  const secondColumnValue = item.sums.dateColumnSums.get(selectedBasedDataColumns.get(ColumnOrder.SECOND).selectedDate);
  const thirdColumnValue = item.sums.dateColumnSums.get(selectedBasedDataColumns.get(ColumnOrder.THIRD).selectedDate);

  const body = {
    '': item.name.toUpperCase(),
    'Date(0)': getCell({
      v: firstColumnValue,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(0)': getCell({
      f: `D${currentIndex}-B${currentIndex}`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(1)': getCell({
      v: secondColumnValue,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(1)': getCell({
      f: `F${currentIndex}-D${currentIndex}`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(2)': getCell({
      v: thirdColumnValue,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(2)': getCell({
      f: `H${currentIndex}-F${currentIndex}`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(3)': getCell({
      v: item.sums.balance,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
  };
  return body;
};

export const getSubTotalMapping = (part: FinancialStatementPart, currentIndex: number) => {
  const len = part.financialStatementItems.length - 1;
  const body = {
    '': 'Total ' + part.partName,
    'Date(0)': getCell({
      f: `SUM(B${currentIndex - len}:B${currentIndex})`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(0)': getCell({
      f: `SUM(C${currentIndex - len}:C${currentIndex })`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(1)': getCell({
      f: `SUM(D${currentIndex - len}:D${currentIndex })`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(1)': getCell({
      f: `SUM(E${currentIndex - len}:E${currentIndex })`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(2)': getCell({
      f: `SUM(F${currentIndex - len}:F${currentIndex })`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(2)': getCell({
      f: `SUM(G${currentIndex - len}:G${currentIndex})`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(3)': getCell({
      f: `SUM(H${currentIndex - len}:H${currentIndex })`,
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
  };
  return body;
};

export const getGrandTotalRow = (headers: string[]) => {
  const rows: Rows = [];
  const row = [];
  const check = grandTotalMapping();
  for (const header of headers) {
    row.push(withCellStyles(check[header], boldGrayBgLeft));
  }
  rows.push(row);
  return rows;

};

export const grandTotalMapping = () => {
  const body = {
    '': 'Check',
    'Date(0)': getCell({
      f: countCheck('B'),
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(0)': getCell({
      f: countCheck('C'),
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(1)': getCell({
      f: countCheck('D'),
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(1)': getCell({
      f: countCheck('E'),
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(2)': getCell({
      f: countCheck('F'),
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Difference(2)': getCell({
      f: countCheck('G'),
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
    'Date(3)': getCell({
      f: countCheck('H'),
      z: formats.thousandsSeparatorToFixed2,
      t: 'n',
    }),
  };
  return body;
};

export const countCheck = (letter: string) => {
  const pc = `${letter}${partnersCapitalIndicator}+`;
  const result = pc + partIndexArray.map((totalI) => `${letter}${totalI}`).join('+');
  return result;
};

export let partIndexArray = [];

export let partnersCapitalIndicator = null;

export let currentIndex = 1;
